import { ReactNode, RefObject, useEffect, useRef } from 'react';

import { fabric } from 'fabric';

import {
  getCanvasScale,
  initCanvasGuidelines,
  initCanvasSnapping,
  resizeCanvas
} from '@/helpers';

type BaseCanvasProps = {
  containerId: string;
  containerClassName?: string;
  canvasClassName?: string;
  canvasOptions?: fabric.ICanvasOptions;
  baseCanvasSizeRef: RefObject<{ width: number; height: number } | null>;
  onCanvasInit?: (canvas: fabric.Canvas) => void;
  children?: ReactNode;
};

export const BaseCanvas = ({
  containerId,
  containerClassName = '',
  canvasClassName = '',
  canvasOptions,
  baseCanvasSizeRef,
  onCanvasInit,
  children
}: BaseCanvasProps) => {
  const canvasHTMLRef = useRef<HTMLCanvasElement | null>(null);
  const canvasRef = useRef<fabric.Canvas | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const onCanvasInitRef = useRef(onCanvasInit);

  const resizeCanvasToContainer = () => {
    const canvas = canvasRef.current;
    const baseSize = baseCanvasSizeRef.current;

    if (!canvas || !baseSize) {
      return;
    }

    const scale =
      Math.round(getCanvasScale(containerId, baseSize.width, baseSize.height) * 100) / 100;

    resizeCanvas(canvas, scale, baseSize.width, baseSize.height);
    canvas.requestRenderAll();
  };

  useEffect(() => {
    onCanvasInitRef.current = onCanvasInit;
  }, [onCanvasInit]);

  useEffect(() => {
    const canvas = new fabric.Canvas(canvasHTMLRef.current, {
      preserveObjectStacking: true,
      ...canvasOptions
    });

    canvasRef.current = canvas;
    onCanvasInitRef.current?.(canvas);

    return () => {
      canvas.dispose();
      canvasRef.current = null;
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const disposeSnapping = initCanvasSnapping(canvas);
    const disposeGuidelines = initCanvasGuidelines(canvas);

    window.addEventListener('resize', resizeCanvasToContainer);

    return () => {
      disposeSnapping();
      disposeGuidelines();
      window.removeEventListener('resize', resizeCanvasToContainer);
    };
  }, []);

  return (
    <div id={containerId} ref={containerRef} className={containerClassName}>
      {children}
      <canvas className={canvasClassName} ref={canvasHTMLRef} />
    </div>
  );
};
